/**
 * The new layout (beta) of the Omni app: one frame for every screen.
 *
 *   ┌──────────────────────────── top bar ─────────────────────────────┐
 *   │ sidebar │ Lykke (agent panel) │ workspace (rounded)      │drawer │
 *   │ Pin or  │ the same Lykke on   │ the page                 │config,│
 *   │ Float   │ every page; input   │                          │details│
 *   │         │ at the bottom       │                          │       │
 *   └─────────┴─────────────────────┴──────────────────────────┴───────┘
 *
 * The sidebar pushes the page (never covers it); Lykke's panel opens and closes (Ctrl J) and keeps her conversation;
 * the right drawer opens over the workspace's right side. On a phone: top bar, the workspace, the sidebar as a
 * sheet from the left and Lykke as a bottom sheet. Light and dark chrome (pages keep their light look inside).
 *
 * App.tsx draws this instead of the classic frame when the person's "New layout (beta)" setting is on.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Menu, SlidersHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/components/ui/utils';
import { AppSidebar } from '@/components/AppSidebar';
import { HeaderSearchBar } from '@/components/HeaderSearchBar';
import { UserProfileMenu } from '@/components/UserProfileMenu';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { BrandBadge } from '@/cowork/BrandBadge';
import { PippiButton } from '@/cowork/pippi/PippiButton';
import { LykkeAvatar } from '@/cowork/lykke/LykkeAvatar';
import { useLykke } from '@/cowork/lykke/LykkeContext';
import beatlyLogo from 'figma:asset/78166de18001e30ac00ef2150ee31fcaaf218dc5.png';
import { ShellProvider, useShell } from './ShellContext';
import { AgentPanel } from './AgentPanel';
import { RightDrawer } from './RightDrawer';
import { ShellSettings } from './ShellSettings';

export interface AppShellV2Props {
  email?: string | null;
  staff: boolean;
  currentPage: string;
  pathname: string;
  onPageChange: (page: string) => void;
  showCowork: boolean;
  clientPages: readonly string[];
  homeBadge: number;
  lastCampaignPath: string | null;
  lastCampaignDisplayName: string | null;
  /** The page needs the whole workspace and scrolls inside itself (Content Hub's split panes). */
  lockScroll?: boolean;
  children: ReactNode;
}

export function AppShellV2(props: AppShellV2Props) {
  return (
    <ShellProvider email={props.email}>
      <Frame {...props} />
    </ShellProvider>
  );
}

function useDark(theme: 'light' | 'dark' | 'system'): boolean {
  const q = '(prefers-color-scheme: dark)';
  const [sys, setSys] = useState(() => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(q).matches);
  useEffect(() => {
    if (theme !== 'system' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(q);
    const on = () => setSys(mq.matches);
    on();
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, [theme]);
  return theme === 'dark' || (theme === 'system' && sys);
}

function Frame({ staff, currentPage, pathname, onPageChange, showCowork, clientPages, homeBadge, lastCampaignPath, lastCampaignDisplayName, lockScroll, children }: AppShellV2Props) {
  const { t } = useTranslation();
  const shell = useShell()!;
  const l = useLykke();
  const dark = useDark(shell.prefs.theme);
  const [sidebarOpen, setSidebarOpen] = useState(shell.prefs.sidebar === 'pinned');
  const [menu, setMenu] = useState(false);
  const phone = shell.isPhone;
  const sidebarW = phone ? 0 : sidebarOpen ? 256 : 64;
  const live = !!l && l.voice !== 'off';

  // The theme reaches the portals (menus, dialogs) too: the attribute sits on <html> while the shell is up.
  useEffect(() => {
    const el = document.documentElement;
    el.setAttribute('data-shell-theme', dark ? 'dark' : 'light');
    return () => el.removeAttribute('data-shell-theme');
  }, [dark]);

  const sidebarProps = { currentPage, onPageChange, showCowork, clientPages, homeBadge, lastCampaignPath, lastCampaignDisplayName, pathname };

  return (
    <div data-omni-shell data-theme={dark ? 'dark' : 'light'} className="flex h-dvh flex-col overflow-clip bg-[var(--shell-bg)] text-[var(--shell-text)]">
      {/* Top bar, full width */}
      <header data-shell-topbar className="relative z-50 flex h-14 shrink-0 items-center gap-1.5 pr-3 lg:pr-4">
        {phone ? (
          <button type="button" onClick={() => setMenu(true)} aria-label={t('nav.openMobileMenu')} className="ml-2 grid h-10 w-10 place-items-center rounded-lg text-[var(--shell-muted)] hover:bg-[var(--shell-hover)]"><Menu className="h-5 w-5" /></button>
        ) : null}
        <div className="flex shrink-0 items-center pl-3 pr-2 lg:pl-5">
          <img src={beatlyLogo} alt={t('nav.logoAlt')} className="h-8 w-auto" style={dark ? { filter: 'brightness(0) invert(1)' } : undefined} />
        </div>
        {!phone && (
          <button type="button" onClick={() => shell.setAgentOpen(!shell.agentOpen)} aria-pressed={shell.agentOpen} aria-label={shell.agentOpen ? 'Hide Lykke' : 'Show Lykke'} title={`${shell.agentOpen ? 'Hide' : 'Show'} Lykke (Ctrl J)`}
            data-shell-lykke-toggle
            className={cn('ml-2 flex h-9 items-center gap-2 rounded-full pl-1 pr-3 text-[13px] font-semibold transition-colors',
              shell.agentOpen ? 'bg-[#be43ff]/10 text-[#7a1fbf]' : 'text-[var(--shell-text)] hover:bg-[var(--shell-hover)]')}>
            <LykkeAvatar size={28} live={live} />
            Lykke
          </button>
        )}
        <div className="min-w-0 flex-1" />
        <PippiButton compact={phone} />
        {!phone && <HeaderSearchBar showCowork={showCowork} lastCampaignPath={lastCampaignPath} lastCampaignDisplayName={lastCampaignDisplayName} />}
        <button type="button" onClick={() => (shell.drawer?.id === 'layout' ? shell.closeDrawer() : shell.openDrawer({ id: 'layout', title: 'Layout', content: <ShellSettings />, width: 360 }))}
          aria-label="Layout settings" title="Layout: theme, sidebar, Lykke" data-shell-settings-button
          className="grid h-9 w-9 place-items-center rounded-lg text-[var(--shell-muted)] hover:bg-[var(--shell-hover)] hover:text-[var(--shell-text)]">
          <SlidersHorizontal className="h-[18px] w-[18px]" />
        </button>
        {!phone && <div className="mx-1.5 h-6 w-px bg-[var(--shell-border-strong)]" />}
        {showCowork && !phone && <BrandBadge />}
        <UserProfileMenu onPageChange={onPageChange} />
      </header>

      {/* Sidebar: Pin or Float, pushes the page */}
      {!phone && (
        <AppSidebar isCollapsed onToggle={() => {}} {...sidebarProps}
          pinMode={shell.prefs.sidebar} onPinModeChange={(m) => shell.setPrefs({ sidebar: m })}
          onExpandedChange={setSidebarOpen} />
      )}
      {phone && (
        <Sheet open={menu} onOpenChange={setMenu}>
          <SheetContent side="left" className="flex w-[min(100vw,20rem)] flex-col gap-0 p-0 [&_[data-slot=sheet-close]]:z-10">
            <SheetTitle className="sr-only">{t('nav.mobileNavTitle')}</SheetTitle>
            <div className="min-h-0 flex-1 overflow-auto pt-12">
              <AppSidebar isMobile isCollapsed onToggle={() => {}} {...sidebarProps} onNavigate={() => setMenu(false)} />
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Lykke, the workspace and the drawer */}
      <div className="relative flex min-h-0 flex-1 gap-2 overflow-clip pb-2 pr-2 transition-[padding] duration-[180ms] ease-[cubic-bezier(0.2,0,0,1)]"
        style={{ paddingLeft: phone ? 8 : sidebarW }}>
        {!phone && <AgentPanel staff={staff} />}
        <main
          data-guide-screen
          data-workspace
          className={cn('relative min-w-0 flex-1 rounded-[16px] border border-[var(--shell-border)] bg-[var(--background)] text-[#1a1a1a]', lockScroll ? 'overflow-hidden' : 'overflow-y-auto overflow-x-hidden')}
          style={{ ['--ws-h' as string]: 'calc(100dvh - 3.5rem - 0.5rem - 2px)' }}
        >
          {children}
        </main>
        <RightDrawer />
      </div>

      {/* Phone: Lykke is a bottom sheet with a button to call her up */}
      {phone && (
        <>
          {shell.agentOpen && <div className="fixed inset-0 z-[65] bg-black/30" onClick={() => shell.setAgentOpen(false)} aria-hidden />}
          <AgentPanel staff={staff} />
          {!shell.agentOpen && (
            <button type="button" onClick={() => shell.focusAgent()} data-shell-lykke-fab
              className="fixed bottom-4 right-4 z-[60] flex h-12 items-center gap-2 rounded-full border border-[var(--shell-border)] bg-[var(--shell-surface)] pl-1.5 pr-4 text-[13.5px] font-semibold text-[var(--shell-text)] shadow-[0_8px_24px_rgba(16,24,40,0.16)]">
              <LykkeAvatar size={36} live={live} />Ask Lykke
            </button>
          )}
        </>
      )}
    </div>
  );
}
